/**
 * The keys a phone does not have, given to the terminal that needs them.
 *
 * See `keybar.ts` for what is missing and why the encoding lives apart from
 * this. What this file decides is only which keys are worth a row of the
 * screen, and in what order.
 *
 * THE ORDER IS THE DESIGN
 * -----------------------
 * The bar scrolls, so whatever is first is what an operator gets without
 * looking for it. The order is by how much trouble the key gets you out of:
 *
 *   1. **Esc** — how both agent CLIs are told no. An operator who opens the
 *      console on a phone because something is waiting has come to press this.
 *   2. **Ctrl** — because Ctrl+C is second, and because it is the modifier that
 *      unlocks two dozen more keys rather than one.
 *   3. **Tab** — completion, and the mode switch on the Claude CLI.
 *   4. **The arrows** — history, and moving inside a line that cannot be
 *      clicked into accurately with a finger.
 *
 * Everything after that is reachable by scrolling and is there because it has
 * no other route on this device, not because it is used often.
 *
 * `onPointerDown` with `preventDefault`, never `onClick`. A tap on a button is
 * a focus change, and a focus change away from xterm's hidden textarea closes
 * the soft keyboard — so with `onClick` the bar would dismiss the keyboard on
 * every press, and the operator would be tapping Esc into a terminal that no
 * longer had focus. Cancelling the pointer event before it becomes a focus
 * change is what keeps the keyboard up, and it is the whole reason this is not
 * an ordinary button handler.
 */

import { useCallback, useRef, useState } from 'react'
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ChevronsLeft,
  ChevronsRight,
  CornerDownLeft,
} from 'lucide-react'
import clsx from 'clsx'

import { Icon } from '@/components/icon'
import { useT } from '@/i18n/context'
import type { MessageKey } from '@/i18n'
import type { BarKey, Latch } from './keybar'
import './KeyBar.css'

/** One key on the bar. Either a sequence key or one of the two latches. */
interface KeyDef {
  /** What it sends, or `null` for a modifier, which sends nothing by itself. */
  key: BarKey | null
  /** The face. A glyph where one exists, otherwise the key's own name. */
  glyph?: typeof ArrowUp
  text?: string
  /** The accessible name, which is what a screen reader and a long-press tooltip
   *  get. Never omitted for a glyph key: an arrow with no name is an arrow. */
  labelKey: MessageKey
  /** Modifier keys are wider and separated; sequence keys are square. */
  modifier?: 'ctrl' | 'alt'
}

const KEYS: KeyDef[] = [
  { key: 'escape', text: 'Esc', labelKey: 'keybar.escape' },
  { key: null, text: 'Ctrl', labelKey: 'keybar.ctrl', modifier: 'ctrl' },
  { key: 'tab', glyph: CornerDownLeft, labelKey: 'keybar.tab' },
  { key: 'left', glyph: ArrowLeft, labelKey: 'keybar.left' },
  { key: 'down', glyph: ArrowDown, labelKey: 'keybar.down' },
  { key: 'up', glyph: ArrowUp, labelKey: 'keybar.up' },
  { key: 'right', glyph: ArrowRight, labelKey: 'keybar.right' },
  { key: null, text: 'Alt', labelKey: 'keybar.alt', modifier: 'alt' },
  { key: 'home', glyph: ChevronsLeft, labelKey: 'keybar.home' },
  { key: 'end', glyph: ChevronsRight, labelKey: 'keybar.end' },
  { key: 'pageUp', text: 'PgUp', labelKey: 'keybar.pageUp' },
  { key: 'pageDown', text: 'PgDn', labelKey: 'keybar.pageDown' },
]

export interface KeyBarProps {
  ctrl: Latch
  alt: Latch
  onLatch: (which: 'ctrl' | 'alt') => void
  onKey: (key: BarKey) => void
  /** False while the pane cannot be typed into — observed, or the lease is
   *  somebody else's. The bar is drawn and disabled rather than removed: it
   *  taking a row and then giving it back would resize the terminal under the
   *  operator's hands, which reflows the program's absolute cursor moves. */
  enabled: boolean
}

export function KeyBar({ ctrl, alt, onLatch, onKey, enabled }: KeyBarProps) {
  const t = useT()
  const bar = useRef<HTMLDivElement>(null)

  /**
   * One tab stop for the whole bar, with the arrows moving inside it.
   *
   * The ARIA toolbar pattern, and not merely for conformance: twelve buttons
   * each taking a tab stop puts twelve presses between an operator's keyboard
   * and the terminal itself, on a device where the bar exists precisely because
   * getting keys *into* the terminal is the hard part.
   *
   * Left and Right rather than Up and Down, because the bar is a row — and the
   * arrows here move focus rather than sending themselves, which is why the
   * handler stops the event: an Arrow key that both moved the focus and typed
   * into the shell would be one press doing two things.
   */
  const [cursor, setCursor] = useState(0)
  const move = useCallback((e: React.KeyboardEvent) => {
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0
    if (step === 0) return
    e.preventDefault()
    setCursor((at) => {
      const next = (at + step + KEYS.length) % KEYS.length
      const buttons = bar.current?.querySelectorAll('button')
      buttons?.[next]?.focus()
      return next
    })
  }, [])

  const press = useCallback(
    (e: React.PointerEvent, def: KeyDef) => {
      // Before anything else: this is what stops the tap becoming a focus change
      // and dismissing the soft keyboard. See the note at the top of the file.
      e.preventDefault()
      if (!enabled) return
      if (def.modifier) onLatch(def.modifier)
      else if (def.key) onKey(def.key)
    },
    [enabled, onLatch, onKey],
  )

  return (
    <div
      ref={bar}
      className="k-keybar k-scroll-none"
      role="toolbar"
      aria-label={t('keybar.title')}
      aria-disabled={!enabled || undefined}
      onKeyDown={move}
      // The bar scrolls sideways, and a scroll region a keyboard cannot reach is
      // a WCAG 2.1.1 failure — which is exactly the state it is in while the
      // pane holds no lease, because every button in it is then disabled and a
      // disabled button is not focusable. Taking the tab stop itself in that one
      // case keeps the region reachable; while the keys are live they carry it
      // between them and the bar must not be a second stop.
      tabIndex={enabled ? -1 : 0}
    >
      {KEYS.map((def, i) => {
        const latch = def.modifier === 'ctrl' ? ctrl : def.modifier === 'alt' ? alt : 'off'
        return (
          <button
            key={def.labelKey}
            type="button"
            className={clsx(
              'k-keybar__key',
              def.modifier && 'k-keybar__key--mod',
              latch !== 'off' && 'k-keybar__key--on',
              latch === 'locked' && 'k-keybar__key--locked',
            )}
            // `aria-pressed` on the modifiers, because a latch is a toggle and
            // the only visible difference is a colour — which is exactly the
            // kind of state a screen reader has no other way to reach.
            aria-pressed={def.modifier ? latch !== 'off' : undefined}
            aria-label={t(def.labelKey)}
            title={t(def.labelKey)}
            disabled={!enabled}
            tabIndex={i === cursor ? 0 : -1}
            onFocus={() => setCursor(i)}
            onPointerDown={(e) => press(e, def)}
            // A tap is a pointer event; Enter and Space on a physical keyboard
            // are a click. Both routes are wired, because this bar is also the
            // only place a keyboard user finds these keys written down.
            onClick={(e) => {
              // Already handled by the pointer, unless the click came from the
              // keyboard — where `detail` is 0 and no pointer event preceded it.
              if (e.detail !== 0) return
              if (!enabled) return
              if (def.modifier) onLatch(def.modifier)
              else if (def.key) onKey(def.key)
            }}
          >
            {def.glyph ? (
              <Icon
                icon={def.glyph}
                size={16}
                // Tab's glyph is the return arrow rotated: there is no Tab glyph
                // in the set, and "Tab" as text is two characters wider than the
                // key can afford beside four arrows.
                className={def.key === 'tab' ? 'k-keybar__tab' : undefined}
              />
            ) : (
              <span className="k-keybar__text">{def.text}</span>
            )}
          </button>
        )
      })}
    </div>
  )
}
